import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const CodeBlock = makeShortcode("CodeBlock");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "custom-models"
    }}>{`Custom Models`}</h1>
    <p>{`Use Firefly's Custom Models API to generate brand-aligned image variations at scale. This page explains concepts relevant to custom models.`}</p>
    <h2 {...{
      "id": "overview"
    }}>{`Overview`}</h2>
    <p>{`With custom models, you can capture and replicate distinctive brand aesthetics, characters, objects, or compositional arrangements.
These models are trained and referenced when generating new images, helping to scale brand-consistent visuals.`}</p>
    <h2 {...{
      "id": "training-modes"
    }}>{`Training modes`}</h2>
    <p>{`Custom models are configured to learn different aspects of visual design:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Subject Models:`}</strong>{` These focus on representing specific characters, products, or objects.
For example, a subject model ensures that your brand mascot is depicted accurately in any scenario.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Style Models:`}</strong>{` These emphasize aesthetic qualities like color palettes, patterns, brush stroke techniques, or illustrative cues, ensuring that all generated visuals share a consistent, recognizable style aligned with your brand's look and feel.`}</p>
      </li>
    </ul>
    <h2 {...{
      "id": "asset-ids"
    }}>{`Asset IDs`}</h2>
    <p>{`Custom models are assets hosted securely by Adobe, offering easy organization, versioning, and reuse. Each custom model has a unique asset ID.`}</p>
    <p>{`With Asset IDs you can manage multiple models by tracking updates and maintaining a clear record of styles, subjects, or structures captured in versions over time.`}</p>
    <p>{`Provide the asset ID in your API requests so that the generated images include the appropriate model's learned characteristics.`}</p>
    <h2 {...{
      "id": "image-generation-with-a-custom-model"
    }}>{`Image generation with a custom model`}</h2>
    <p>{`Leverage a custom model to generate images by using the `}<inlineCode parentName="p">{`/v3/images/generate-async`}</inlineCode>{` endpoint and including the header `}<inlineCode parentName="p">{`x-model-version`}</inlineCode>{`. This specifies the model version used for generating the image.`}</p>
    <h3 {...{
      "id": "example-requests"
    }}>{`Example requests`}</h3>
    <CodeBlock slots="heading, code" repeat="3" languages="bash, Python, JavaScript" mdxType="CodeBlock" />
    <h4 {...{
      "id": "curl"
    }}>{`cURL`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`curl --request POST 'https://firefly-api.adobe.io/v3/images/generate-async' \\
--header 'x-model-version: image3_custom' \\
...
--data "{
    \\"prompt\\": \\"An almond seed in a warm setting\\",
    \\"customModelId\\": \\"\${CUSTOM_MODEL_ID}\\"
  }"
`}</code></pre>
    <h4 {...{
      "id": "python"
    }}>{`Python`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`def generate_images_with_custom_model(access_token, custom_model_id):
    url = 'https://firefly-api.adobe.io/v3/images/generate-async'
    headers = {
      'x-model-version':'image3_custom',
      ...
    }

    data = {
        "prompt": "An almond seed in a warm setting",
        "customModelId": custom_model_id
    }

    response = requests.post(url, headers=headers, json=data)
    response.raise_for_status()
    result = response.json()
    print("Generate Image Response:", result)
    return result

`}</code></pre>
    <h4 {...{
      "id": "javascript"
    }}>{`JavaScript`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`async function generateImagesWithCustomModel(accessToken, customModelId) {
  const config = {
    method: "post",
    url: "https://firefly-api.adobe.io/v3/images/generate-async",
    headers: {
    "x-model-version":"image3_custom",
    ...
  },
  data: JSON.stringify({
      "prompt": "An almond seed in a warm setting",
      "customModelId": customModelId
    })
  };

  const response = await axios.request(config);
  return response.data;
}
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      